import { X, Loader2, CheckCircle2, XCircle, Minimize2, Maximize2, RefreshCw, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useState } from 'react';
import { formatNumber } from '@/utils/formatNumber';

export interface ProcessingFile {
  id: string;
  filename: string;
  status: 'uploading' | 'processing' | 'complete' | 'failed' | 'cancelled';
  progress?: number;
  processingStartTime?: number;
  error?: string;
}

interface ProcessingPanelProps {
  files: ProcessingFile[];
  onCancel: (fileId: string) => void;
  onCancelAll: () => void;
  onClose: () => void;
  onRetry?: (fileId: string) => void;
  onPause?: () => void;
  onContinue?: () => void;
  isPaused?: boolean;
}

export function ProcessingPanel({ files, onCancel, onCancelAll, onClose, onRetry, onPause, onContinue, isPaused = false }: ProcessingPanelProps) {
  const [isMinimized, setIsMinimized] = useState(true);

  if (files.length === 0) return null;

  const activeFiles = files.filter(f => 
    f.status === 'uploading' || f.status === 'processing'
  );
  const uploadingFiles = files.filter(f => f.status === 'uploading');
  const completedCount = files.filter(f => f.status === 'complete').length;
  const failedCount = files.filter(f => f.status === 'failed' || f.status === 'cancelled').length;

  const total = files.length;
  const progressSum = files.reduce((sum, f) => {
    let p = 0;
    if (f.status === 'uploading') p = f.progress ?? 0;
    else if (f.status === 'processing' || f.status === 'complete' || f.status === 'failed' || f.status === 'cancelled') p = 100;
    return sum + p;
  }, 0);
  const overallProgress = total > 0 ? progressSum / total : 0;

  const getStatusIcon = (status: ProcessingFile['status']) => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin text-primary" />;
      case 'complete':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-destructive" />;
    }
  };

  const getStatusText = (status: ProcessingFile['status']) => {
    switch (status) {
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Processing with AI...';
      case 'complete':
        return 'Complete';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-lg z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold">
            Processing {formatNumber(files.length)} {files.length === 1 ? 'file' : 'files'}
            {activeFiles.length > 0 && (
              <span className="text-muted-foreground font-normal">
                {' '}({formatNumber(activeFiles.length)} {activeFiles.length === 1 ? 'file' : 'files'} left)
              </span>
            )}
          </h3>
          <div className="text-sm text-muted-foreground">
            {completedCount > 0 && <span className="text-green-500">{formatNumber(completedCount)} complete</span>}
            {completedCount > 0 && failedCount > 0 && <span className="mx-1">•</span>}
            {failedCount > 0 && <span className="text-destructive">{formatNumber(failedCount)} failed</span>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onPause && onContinue && (
            <>
              {!isPaused && (uploadingFiles.length > 0 || activeFiles.length > 0) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onPause}
                  className="gap-2"
                >
                  <Pause className="w-4 h-4" />
                  Pause
                </Button>
              )}
              {(isPaused || files.some(f => f.status === 'cancelled')) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onContinue}
                  className="gap-2"
                >
                  <Play className="w-4 h-4" />
                  Continue
                </Button>
              )}
            </>
          )}
          {activeFiles.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancelAll}
              className="text-destructive"
            >
              Cancel All
            </Button>
          )}
          {failedCount > 0 && activeFiles.length === 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label="Clear completed and failed"
            >
              Clear All
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? "Maximize" : "Minimize"}
            aria-label={isMinimized ? "Maximize processing panel" : "Minimize processing panel"}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            title="Close"
            aria-label="Close processing panel"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="px-4 pb-3">
        <Progress value={overallProgress} className="h-1 w-full" />
      </div>

      {/* File list */}
      {!isMinimized && (
        <div className="flex-1 overflow-y-auto p-4 pt-0 space-y-3 max-h-[350px]">
        {files.map((file) => (
          <div
            key={file.id}
            className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg"
          >
            <div className="flex-shrink-0">
              {getStatusIcon(file.status)}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium truncate">{file.filename}</p>
                <span className="text-xs text-muted-foreground ml-2">
                  {getStatusText(file.status)}
                </span>
              </div>
              
              {file.error && (file.status === 'failed' || file.status === 'cancelled') && (
                <p className="text-xs text-destructive mt-1">{file.error}</p>
              )}
              
              {file.status === 'uploading' && file.progress !== undefined && (
                <Progress value={file.progress} className="h-1" />
              )}
              
              {file.status === 'processing' && (
                <Progress value={100} className="h-1 animate-pulse" />
              )}
            </div>

            {(file.status === 'uploading' || file.status === 'processing') && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onCancel(file.id)}
                className="flex-shrink-0 h-8 w-8"
                title="Cancel upload"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            
            {(file.status === 'failed' || file.status === 'cancelled') && (
              <>
                {onRetry && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRetry(file.id)}
                    className="flex-shrink-0 h-8 w-8"
                    title="Retry AI processing"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onCancel(file.id)}
                  className="flex-shrink-0 h-8 w-8"
                  title="Remove from list"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        ))}
        </div>
      )}
    </div>
  );
}
